import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, Output } from "@angular/core";
import { CsSegmentButtonBase } from "./cs-segment-button-interface";
import { CsSegmentComponent } from "./cs-segment";

@Component({
    selector: 'cs-segment-button',
    templateUrl: 'cs-segment-button.html',
    host: {
      'class': 'segment-button',
      '[attr.role]': 'role',
      '[attr.tabindex]': 'tabIndex',
      '[attr.aria-pressed]': 'ariaPressed',
      '[attr.aria-disabled]': 'ariaDisabled',
    },
    providers: [{ provide: CsSegmentButtonBase, useExisting: CsSegmentButtonComponent }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CsSegmentButtonComponent extends CsSegmentButtonBase implements AfterViewInit {
  @Input() value: any;
  @Output() segmentClick = new EventEmitter<Event>();

  @HostBinding('class.segment-activated') isActive = false;

  // Whether this button can actually do anything when it is activated: either it carries
  // a value for the group to select, or the caller listens for its click. Not every one
  // does -- the virtual keyboard's `.static-unit` button just displays the unit when
  // there is nothing to choose. Such a button must not be announced as a button or take
  // a tab stop, or a keyboard user is offered a control that does nothing.
  //
  // This gates the accessibility attributes only, deliberately not onClick: a caller
  // whose `[value]` is momentarily undefined would otherwise stop working with no error,
  // and a non-interactive button is already unreachable -- `.static-unit` sets
  // `pointer-events: none`, and without a tab stop the keyboard cannot get to it either.
  get isInteractive(): boolean {
    return this.value !== undefined || this.segmentClick.observers.length > 0;
  }

  get role(): string | null {
    return this.isInteractive ? 'button' : null;
  }

  get ariaPressed(): boolean | null {
    return this.isInteractive ? this.isActive : null;
  }

  // Said on the BUTTON, not only on the group. cs-segment carries no ARIA role, so it forms
  // no group a screen reader would propagate a state from -- with the attribute only on the
  // host, a switched-off dial still announced each position as a working toggle button, and
  // activating one did nothing with no explanation. `tabindex="-1"` does not help here:
  // browse mode reaches the button anyway. null rather than false so the attribute is absent
  // on an enabled control instead of reading as noise.
  get ariaDisabled(): true | null {
    return this.segmentComponent.disabled ? true : null;
  }

  // Reachable by Tab, unless the whole group is switched off. A segment button is a
  // custom element, so it has no tab stop of its own until one is given to it -- which
  // is also why there was no visible focus ring to style before (#5210). null leaves the
  // attribute off entirely, which is what keeps a non-interactive button out of the tab
  // order rather than merely giving it -1.
  get tabIndex(): number | null {
    if (!this.isInteractive) {
      return null;
    }
    return this.segmentComponent.disabled ? -1 : 0;
  }

  constructor(private segmentComponent: CsSegmentComponent,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef) {
    super();
  }

  ngAfterViewInit() {
    let element = this.elementRef.nativeElement as HTMLElement;
    element.addEventListener('click', (ev: Event) => {
      this.onClick(ev);
    });
    // also listen for touchstart to match keyboard behavior
    element.addEventListener('touchstart', (ev: Event) => {
      this.onClick(ev);
    });

    // role=button plus a tab stop advertise keyboard support, so it has to work
    element.addEventListener('keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        this.onClick(ev);
      }
    });
  }

  private onClick(ev: Event) {
    // a disabled group refuses every route in, not only the pointer one its CSS blocks
    if (this.segmentComponent.disabled) {
      return;
    }

    // prevent default behavior and stop propagation to avoid focus changes
    ev.preventDefault();
    ev.stopPropagation();

    // if there's a custom click handler, emit it
    if (this.segmentClick.observers.length > 0) {
      this.segmentClick.emit(ev);
    } else {
      // otherwise use standard segment selection
      this.segmentComponent.select(this);
      this.select();
    }
  }

  deselect(): void {
    this.isActive = false;
    this.cdr.markForCheck();
  }

  getValue(): any {
    return this.value;
  }

  select(): void {
    this.isActive = true;
    this.cdr.markForCheck();
  }
}
